import { z } from 'zod';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { WriteBackToLeverStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/lever-write-back';
import { WriteBackOverrideRule } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';

/** Strategy for resume score writeback. Currently only Lever; extend union when adding more. */
export type WorkflowCandidateResumeScoreWriteBackStrategy = z.infer<
  typeof WriteBackToLeverStrategy
>;

export const WorkflowCandidateResumeScoreWriteBackScoreSourceType = z.enum([
  'transform',
  'composite-resume-grader-data-set',
]);

export const WorkflowCandidateResumeScoreWriteBackScoreSourceTypes =
  Object.values(WorkflowCandidateResumeScoreWriteBackScoreSourceType.Values);

export const WorkflowCandidateResumeScoreWriteBackScoreSourceTypeDisplayValueMap: Record<
  WorkflowCandidateResumeScoreWriteBackScoreSourceType,
  string
> = {
  transform: 'Transform',
  'composite-resume-grader-data-set': 'Composite Resume Grader Data Set',
};

export const WorkflowCandidateResumeScoreWriteBackScoreSourceTypesDropdowns =
  WorkflowCandidateResumeScoreWriteBackScoreSourceTypes.map((value) => ({
    optionValue: value,
    optionLabel:
      WorkflowCandidateResumeScoreWriteBackScoreSourceTypeDisplayValueMap[
        value
      ],
  }));

export type WorkflowCandidateResumeScoreWriteBackScoreSourceType = z.infer<
  typeof WorkflowCandidateResumeScoreWriteBackScoreSourceType
>;

export const WorkflowCandidateResumeScoreWriteBackScoreTransformSource =
  z.object({
    type: z.intersection(
      WorkflowCandidateResumeScoreWriteBackScoreSourceType,
      z.literal('transform'),
    ),
  });

export type WorkflowCandidateResumeScoreWriteBackScoreTransformSource = z.infer<
  typeof WorkflowCandidateResumeScoreWriteBackScoreTransformSource
>;

export const WorkflowCandidateResumeScoreWriteScoreCompositeDataSetSource =
  z.object({
    type: z.intersection(
      WorkflowCandidateResumeScoreWriteBackScoreSourceType,
      z.literal('composite-resume-grader-data-set'),
    ),
    /**
     * The source of the score.
     */
    dataSetId: z.string(),
  });

export type WorkflowCandidateResumeScoreWriteScoreCompositeDataSetSource =
  z.infer<typeof WorkflowCandidateResumeScoreWriteScoreCompositeDataSetSource>;

export const WorkflowCandidateResumeScoreWriteBackScoreSource = z.union([
  WorkflowCandidateResumeScoreWriteBackScoreTransformSource,
  WorkflowCandidateResumeScoreWriteScoreCompositeDataSetSource,
]);

/**
 * Details for a workflow that writes back hiring readiness scores.
 */
export const WorkflowCandidateResumeScoreWriteBackV2ConfigOption = z.object({
  groupName: z.string(),
  strategy: WriteBackToLeverStrategy.optional(),
  mode: WorkflowWriteBackMode,

  /**
   * Optional list of rules to override the writeback value based on the resume score.
   * Rules are evaluated in order, and the first matching rule is applied.
   */
  overrideRules: z.array(WriteBackOverrideRule).optional(),
});

export type WorkflowCandidateResumeScoreWriteBackV2ConfigOption = z.infer<
  typeof WorkflowCandidateResumeScoreWriteBackV2ConfigOption
>;

export const WorkflowCandidateResumeScoreWriteBackV2Config = z.object({
  options: z.array(WorkflowCandidateResumeScoreWriteBackV2ConfigOption),

  /**
   * Optional fallback configuration for applicants without a group or with an empty group.
   * If not provided, applicants without groups will be skipped.
   */
  noGroupConfig: WorkflowCandidateResumeScoreWriteBackV2ConfigOption.omit({
    groupName: true,
  }).optional(),
});

export type WorkflowCandidateResumeScoreWriteBackV2Config = z.infer<
  typeof WorkflowCandidateResumeScoreWriteBackV2Config
>;

/**
 * Determines which configuration to use for an applicant based on their group assignment.
 * This handles both V2 (group-based) and V1 (legacy) configurations.
 */
export const determineCandidateResumeApplicantConfig = ({
  groupName,
  v2Config,
  legacyStrategy,
  legacyMode,
}: {
  groupName: string | undefined;
  v2Config: WorkflowCandidateResumeScoreWriteBackV2Config | undefined;
  legacyStrategy?: WorkflowCandidateResumeScoreWriteBackStrategy;
  legacyMode?: WorkflowWriteBackMode;
}):
  | {
      type: 'success';
      strategy: WriteBackToLeverStrategy;
      mode: WorkflowWriteBackMode;
      overrideRules: WriteBackOverrideRule[];
      groupName: string;
      isNoGroupFallback: boolean;
    }
  | { type: 'skip'; reason: string }
  | { type: 'error'; reason: string } => {
  // V2 Configuration (group-based)
  if (v2Config) {
    const trimmedGroup = groupName?.trim() || '';

    // Check if no group or empty group
    if (!trimmedGroup) {
      const noGroup = v2Config.noGroupConfig;
      if (noGroup?.strategy) {
        return {
          type: 'success',
          strategy: noGroup.strategy,
          mode: noGroup.mode,
          overrideRules: noGroup.overrideRules ?? [],
          groupName: '(No Group - Using Fallback)',
          isNoGroupFallback: true,
        };
      }
      return {
        type: 'skip',
        reason: 'No group assigned and no fallback configuration',
      };
    }

    // Find matching group
    const matchingGroup = v2Config.options.find(
      (opt) => opt.groupName === trimmedGroup,
    );

    if (!matchingGroup) {
      return {
        type: 'error',
        reason: `Group "${trimmedGroup}" not found in configuration`,
      };
    }

    if (!matchingGroup.strategy) {
      return {
        type: 'skip',
        reason: `Group "${trimmedGroup}" has no strategy configured`,
      };
    }

    return {
      type: 'success',
      strategy: matchingGroup.strategy,
      mode: matchingGroup.mode,
      overrideRules: matchingGroup.overrideRules ?? [],
      groupName: matchingGroup.groupName,
      isNoGroupFallback: false,
    };
  }

  // V1 Configuration (legacy)
  if (legacyStrategy && legacyMode) {
    return {
      type: 'success',
      strategy: legacyStrategy,
      mode: legacyMode,
      overrideRules: [],
      groupName: '(Legacy Configuration)',
      isNoGroupFallback: false,
    };
  }

  return {
    type: 'skip',
    reason: 'No configuration available',
  };
};

export const WorkflowCandidateResumeScoreWriteBackDetails = z.object({
  type: z.literal('candidateResumeScoreWriteBack'),

  /**
   * The strategy for writing back.
   */
  strategy: WriteBackToLeverStrategy.optional(),

  /**
   * The mode for writing back.
   */
  mode: WorkflowWriteBackMode.optional(),

  /**
   * V2 Write Back Config (group-based).
   */
  v2Config: WorkflowCandidateResumeScoreWriteBackV2Config.optional(),

  /**
   * The number of previous days of applicants to consider for writing back.
   */
  numPreviousDaysOfApplicants: z.number().optional(),

  /**
   * Number of samples to send
   */
  numberOfSamplesToSend: z.number().optional(),

  /**
   * The source of the score.
   */
  scoreSource: WorkflowCandidateResumeScoreWriteBackScoreSource.default({
    type: 'transform',
  }),

  /** When true, include score explanation text in the writeback note. */
  explanationsEnabled: z.boolean().optional(),
});

export type WorkflowCandidateResumeScoreWriteBackDetails = z.infer<
  typeof WorkflowCandidateResumeScoreWriteBackDetails
>;
